import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { TicketsAPI } from "../api/tickets";

export default function Dashboard() {
  const { hasPermission } = useAuth();

  const [assignedTickets, setAssignedTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [ticketsError, setTicketsError] = useState("");

  const shortcuts = [
    {
      href: "/helpdesk/dashboard",
      label: "Helpdesk Dashboard",
      show: true,
    },
    {
      href: "/helpdesk/tickets",
      label: "Tickets",
      show: true,
    },
    {
      href: "/helpdesk/problems",
      label: "Problems",
      show: true,
    },
    {
      href: "/helpdesk/settings/branding",
      label: "Branding",
      show: hasPermission("helpdesk.settings"),
    },
    {
      href: "/helpdesk/settings/notifications",
      label: "Notifications",
      show: hasPermission("helpdesk.settings"),
    },
    {
      href: "/helpdesk/settings/rules",
      label: "Rules",
      show: hasPermission("helpdesk.automations.manage"),
    },
    {
      href: "/dev-notes",
      label: "Dev Notes",
      show: hasPermission("access.dev-notes"),
    },
    {
      href: "/admin/statuses",
      label: "Statuses",
      show: hasPermission("helpdesk.settings"),
    },
    {
      href: "/admin/categories",
      label: "Categories",
      show: hasPermission("helpdesk.settings"),
    },
    {
      href: "/admin/departments",
      label: "Departments",
      show: hasPermission("helpdesk.settings"),
    },
    {
      href: "/admin/users",
      label: "Users",
      show: hasPermission("staff.manage"),
    },
    {
      href: "/admin/roles",
      label: "Roles",
      show: hasPermission("rbac.manage"),
    },
    {
      href: "/helpdesk/kb",
      label: "Knowledge Base",
      show: hasPermission("manage.kb"),
    },
    {
      href: "/admin/schools",
      label: "Schools",
      show: hasPermission("admin.schools.manage"),
    },
    {
      href: "/leave/admin",
      label: "Leave",
      show: hasPermission("approve.leave"),
    },
    {
      href: "/admin/access-control/jobs",
      label: "Access Control",
      show: hasPermission("access_control.manage"),
    },
  ].filter((item) => item.show);

  useEffect(() => {
    let cancelled = false;

    async function loadAssignedTickets() {
      try {
        setTicketsLoading(true);
        setTicketsError("");

        const tickets = await TicketsAPI.assignedToMe();

        if (!cancelled) {
          setAssignedTickets(Array.isArray(tickets) ? tickets : []);
        }
      } catch (err) {
        console.error("Failed to load assigned tickets:", err);

        if (!cancelled) {
          setTicketsError("Failed to load your assigned tickets.");
        }
      } finally {
        if (!cancelled) {
          setTicketsLoading(false);
        }
      }
    }

    loadAssignedTickets();

    return () => {
      cancelled = true;
    };
  }, []);

  function formatDate(value) {
    if (!value) {
      return "—";
    }

    return new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold">Welcome to Nightjar</h1>

      {shortcuts.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2">
          {shortcuts.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              {item.label}
            </Link>
          ))}
        </div>
      )}

      <div className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Tickets Assigned to Me
          </h2>

          <Link
            to="/helpdesk/tickets"
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            View all tickets
          </Link>
        </div>

        <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white">
          {ticketsLoading ? (
            <div className="p-6 text-sm text-gray-500">
              Loading tickets...
            </div>
          ) : ticketsError ? (
            <div className="p-6 text-sm text-red-600">
              {ticketsError}
            </div>
          ) : assignedTickets.length === 0 ? (
            <div className="p-6 text-sm text-gray-500">
              You have no open tickets assigned to you.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Ticket
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Title
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      School
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Department
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Updated
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-100">
                  {assignedTickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      className="hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium">
                        <Link
                          to={`/helpdesk/tickets/${ticket.id}`}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          #{ticket.id}
                        </Link>
                      </td>

                      <td className="px-4 py-3 text-sm text-gray-900">
                        <Link
                          to={`/helpdesk/tickets/${ticket.id}`}
                          className="hover:underline"
                        >
                          {ticket.title}
                        </Link>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        {ticket.school_name || "—"}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        {ticket.department_name || "—"}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                          {ticket.status_name || "Unknown"}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">
                        {formatDate(ticket.updated_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}